/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect } from 'react';
import { DataTable } from '@scuf/datatable';
import { SplitPanelUtil } from '../../commonUtils/SplitPanelUtil';
import {
  Button,
  Select,
  Badge,
  Popup,
  Icon,
  VerticalMenu,
  Tab,
  Tooltip
} from '@scuf/common';
import { Notification } from '@scuf/common';
import {
  transformUserList,
  ICellData,
  statusBadgeColor,
  transformBulkUserList
} from '../onboardingUtils';
import './UserList.scss';
import { consumeAPIs, IAPIPackage } from '../../commonUtils/APIContext';
import useFetch, { IUseFetch } from '../../commonUtils/useFetch';
import {
  IOrganisationResponse,
  IOrganisation,
  IOrganisationUsers,
  EModalOperations,
  IPermission,
  IRoleResponse
} from '../onboardingModels';
import { IOption } from '@scuf/common/dist/components/Select/ISelectProps';
import toasts from '../../commonUtils/toasts';
import moment from 'moment';
import { ResendUserActivation } from './ResendUserActivation';
import { UserModal } from './UserModal';
import i18next from '../../../i18next';
import { useAuthService } from '../../../auth/hooks/useAuthService';
import { DeleteAlertModal } from './DeleteAlertModal';
import { BulkUpladUserModal, ToastNotification } from './BulkUpladUserModal';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { IOrganisationState, setSelectedGatewayOrganisation, setSelectedOrganisation } from '../../commonUtils/Redux/Global/globalShared';
import { set } from 'lodash';

interface IUserListProps extends IAPIPackage {}
interface BulkUploadUser {
  fileProcessId: number;
  orgId: string;
  userId: string;
  fileName: string;
  blobPath: string;
  createdBy: string;
  createdAt: string;
  status: string;
}
export const UserList = (props: IUserListProps) => {
  const { baseURL, hasPermission, user } = useAuthService();
  const userProfileDetails = useSelector(
    (state: IOrganisationState) => state.globalShared.userList
  );
  const organisationList = useSelector(
    (state: IOrganisationState) => state.globalShared.organisationList
  );
  const [data, setData] = useState<IOrganisationUsers[]>([]);
  const [getBulkUserdata, setBulkUserData] = useState<IOrganisationUsers[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState<number>(18);
  const [isLoadingOrganisationUsers, setLoadingOrganisationUsers] = useState(
    false
  );
  const [panelSelectedRow, setPanelSelectedRow] = useState<any>();
  const [userEmail, setUserEmail] = useState<any>();
  const [uploadUserDataSuccess, setUploadUserDataSuccess] = useState<boolean>(
    false
  );
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [organisationSelected, setOrganisationSelected] = useState<
    IOrganisation
  >();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [getBulkUpladPopupOpen, setBulkUploadPopupOpen] = useState(false);
  const [selectedModalMode, setSelectedModalMode] = useState<EModalOperations>(
    EModalOperations.READ
  );
  const [bulkUserDetails, setBulkUserDetails] = useState<BulkUploadUser[]>([]);
  const [
    organisationData,
    isLoadingOrganisationData,
    errorOrganisationData,
    fetchOrganisationData
  ]: IUseFetch<IOrganisationResponse> = useFetch(
    `${props.baseURL}/api/RootOrganisations/${props.offering}`,
    props.authFetch,
    undefined,
    true
  );
  const flags = useFlags();
  const [
    permissions,
    isLoadingPermissions,
    errorPermissions,
    fetchPermissions
  ]: IUseFetch<IPermission[]> = useFetch(
    `${baseURL}/api/v1/users/me/orgs/${organisationSelected ? organisationSelected.id : undefined}/privileges?scopes=UserManagement`,
    props.authFetch,
    undefined,
    true
  );

  const [
    roleList,
    isLoadingRoleList,
    errorRoleList,
    getAssignableRoles
  ]: IUseFetch<IRoleResponse> = useFetch(
    organisationSelected
      ? `${props.baseURL}/api/v1/users/me/orgs/${organisationSelected.id}/assignable-roles`
      : '',
    props.authFetch,
    undefined,
    false
  );

  const environmentVariables = (window as any).ENV;
  const dispatch = useDispatch();

   useEffect(() => {
      if (
        organisationList &&
        organisationList &&
        organisationList.length > 0
      ) {
        // Set org to the first and only entry if only 1
        if (organisationSelected === null) {
          dispatch(
            setSelectedOrganisation(
              organisationList.find(item => item.zuoraAccountNo !== null)
            )
          );
        }
      }
    }, [organisationList]);

  useEffect(() => {
    if(organisationSelected && flags.bulkUploadUser){
    // Function to start polling
    const startPolling = () => {
     // Initial call
      const intervalId = setInterval(() => {
        fetchBulkUploadUser(intervalId);
      }, environmentVariables.BULKUPLOADDETAILSPOLLINGTIME !== undefined && environmentVariables.BULKUPLOADDETAILSPOLLINGTIME !== '' ? environmentVariables.BULKUPLOADDETAILSPOLLINGTIME : 60000); // 60000 ms = 1 minute
      fetchBulkUploadUser(intervalId);
      return intervalId; // Return intervalId for cleanup
    };

    const intervalId = startPolling();

    return () => clearInterval(intervalId); // Cleanup interval on component unmount
    }

  }, [organisationSelected,flags.bulkUploadUser,uploadUserDataSuccess]); // Empty dependency array ensures this runs once on mount


  useEffect(() => {
    if (errorRoleList) {
      toasts.error(i18next.t('errorFetchingRoles'));
    }
  }, [isLoadingRoleList]);

  const fetchOrganisationUserList = async () => {
    setLoadingOrganisationUsers(true);
    const rootNodeId = organisationSelected
      ? organisationSelected.id
      : undefined;
    const getRegisteredUsers = props.authFetch(
      `${props.baseURL}/api/v1/registered-users/orgs/${rootNodeId}`,
      {
        method: 'GET'
      }
    );

    const getActivatedUsers = props.authFetch(
      `${props.baseURL}/api/v1/users/orgs/${rootNodeId}`,
      {
        method: 'GET'
      }
    );

    const [registeredUsers, activatedUsers] = await Promise.all([
      getRegisteredUsers,
      getActivatedUsers
    ]);

    const usersList: IOrganisationUsers[] = [];
    let apiResult = false;
    if (registeredUsers.status === 200) {
      apiResult = apiResult || true;
      const rUsersRes = await registeredUsers.json();
      const rUsers = rUsersRes.resources;
      if (rUsersRes && rUsers && rUsers.length > 0) {
        transformUserList(rUsers);
        usersList.push(...rUsers);
      }
    }

    if (activatedUsers.status === 200) {
      apiResult = apiResult || true;
      const aUsersRes = await activatedUsers.json();
      const aUsers = aUsersRes.resources;
      if (aUsersRes && aUsers && aUsers.length > 0) {
        transformUserList(aUsers);
        usersList.push(...aUsers);
      }
    }
    setData(usersList);
    if (!apiResult) {
      toasts.error(i18next.t('userListError'));
    }
    setLoadingOrganisationUsers(false);
  };

  const fetchBulkUploadUser = async (intervalId: string | number | NodeJS.Timeout | undefined) => {
    const rootNodeId = organisationSelected
    ? organisationSelected.id
    : undefined;
    const userId = userProfileDetails?.id ? userProfileDetails.id : undefined;
    const getRegisteredUsers = props.authFetch(
      `${props.baseURL}/api/v1/bulkuserupload/orgs/${rootNodeId}/users/${userId}/fileprocess`,
      {
        method: 'GET'
      }
    );
    const [registeredUsers] = await Promise.all([
      getRegisteredUsers,
    ]);
   
    if (registeredUsers?.status === 200) {
     
      const rUsersRes = await registeredUsers.json();
      setBulkUserDetails([rUsersRes]);
      let bulkStatus = rUsersRes && Object.keys(rUsersRes).length !== 0 ? rUsersRes.status : '';
    let bulkArray = ['','Completed']
    if (bulkArray.includes(bulkStatus)) {
      clearInterval(intervalId); // Stop polling
    }
     
    }else{
      setBulkUserDetails([])
    }
    
  }

  useEffect(()=>{
    let bulkResult = bulkUserDetails.length === 0 || bulkUserDetails.length > 0 && bulkUserDetails[0].status === 'Completed'
    if(bulkResult){
      setActiveTabIndex(0)
    }
 
  },[bulkUserDetails])
  const isEmptyObject = (obj: object): boolean => {
    return Object.keys(obj).length === 0 && obj.constructor === Object
  };
  const fetchBulkUploadUserList = async () => {
    setLoadingOrganisationUsers(true);
    const fileprocessid = bulkUserDetails.length > 0 ? bulkUserDetails[0].fileProcessId : '';
    const rootNodeId = organisationSelected
    ? organisationSelected.id
    : undefined;
    const getRegisteredUsers = props.authFetch(
      `${props.baseURL}/api/v1/bulkuserupload/orgs/${rootNodeId}/fileprocessdetails/${fileprocessid}`,
      {
        method: 'GET'
      }
    );
    const [registeredUsers] = await Promise.all([
      getRegisteredUsers,
    ]);
    let bulkUsersList: any[] = [];
    let apiResult = false;
    if (registeredUsers.status === 200) {
      apiResult = apiResult || true;
      const rUsersRes = await registeredUsers.json();

      if (!isEmptyObject(rUsersRes)) {
        bulkUsersList = transformBulkUserList([rUsersRes]);
      
      }
    }
    setBulkUserData(bulkUsersList);
    if (!apiResult) {
      toasts.error(i18next.t('userListError'));
    }
    setLoadingOrganisationUsers(false);
  }

  const updateOrganisationUserList = (user: IOrganisationUsers) => {
    let userMatchFound = false;
    for (let i = 0; i < data.length; i++) {
      if (data[i].id === user.id) {
        userMatchFound = true;
        data[i].registrationStatus = user.registrationStatus;
        data[i].status = user.registrationStatus.type;
        break;
      }
    }

    if (userMatchFound) {
      setData([...data]);
    } else {
      fetchOrganisationUserList();
    }
  };
useEffect(()=>{
  if(activeTabIndex === 1 && flags.bulkUploadUser){
    fetchBulkUploadUserList();
  }
},[activeTabIndex])

  useEffect(() => {
    if (errorOrganisationData) {
      toasts.error(i18next.t('organisationListError'));
    }
  }, [errorOrganisationData]);

  useEffect(() => {
    if (
      organisationData &&
      organisationData.resources &&
      organisationData.resources.length > 0
    ) {
      // Set org to the first and only entry if only 1
      setOrganisationSelected(organisationData.resources[0]);
    }
  }, [organisationData]);

  useEffect(() => {
    if (organisationSelected) {
      fetchOrganisationUserList();
      fetchPermissions();
      getAssignableRoles();
    }
  }, [organisationSelected]);

  useEffect(() => {
    fetchOrganisationData();
  }, []);

  const orgDataToSelectOptions = () => {
    const orgDataOptions =
      organisationData && organisationData.resources
        ? organisationData.resources.map(org => {
            const option: IOption = { value: org.id, text: org.name };
            return option;
          })
        : [];
    return orgDataOptions;
  };

  const selectOrgFromDropdown = (orgId: string) => {
    return organisationData && organisationData.resources
      ? organisationData.resources.find(org => orgId === org.id)
      : undefined;
  };

  const invitationStatusRenderer = (cellData: ICellData) => {
    return (
      <div className="name-column">
        <Badge
          color={statusBadgeColor(cellData.rowData.registrationStatus.type)}
          empty={true}
        />
        <span
          className="name-text"
          onClick={() => setPanelSelectedRow(cellData.rowData)}
        >{`${cellData.value}`}</span>
      </div>
    );
  };

  const invitationBulkStatusRenderer = (cellData: ICellData) => {
    return (
      <div className="name-column">
        {/* <Badge
          color={statusBadgeColor(cellData.rowData.registrationStatus.type)}
          empty={true}
        /> */}
        <span
          className="name-text"
          onClick={() => setPanelSelectedRow(cellData.rowData)}
        >{`${cellData.value}`}</span>
      </div>
    );
  };

  const userStatusRenderer = (cellData: ICellData) => {
    return (
      <div className="status-column">
        <span className="status-text">{cellData.value.type}</span>
        <span>
          {moment(cellData.value.dateTime).format('DD/MM/YYYY hh:mm A')}
        </span>
        {cellData.rowData.registrationStatus.type.toUpperCase() !==
          'REGISTERED' &&
          organisationSelected && (
            <ResendUserActivation
              baseURL={props.baseURL}
              authFetch={props.authFetch}
              offering={props.offering}
              email={cellData.rowData.email}
              organisation={organisationSelected}
              onResend={(user: IOrganisationUsers) =>
                updateOrganisationUserList(user)
              }
            />
          )}
      </div>
    );
  };
  const bulkUserStatusRenderer = (cellData: ICellData) => {
    return (
      <div className="status-column">
        <span className="status-text">{cellData.rowData.status}</span>
        {cellData.rowData.status === 'Failed' && <Tooltip element={<Icon name="badge-alert" size="small" color="#ffC627" />} content={cellData.rowData.reason} position="top right" />}
        {/* <span>
          {moment(cellData.value.dateTime).format('DD/MM/YYYY hh:mm A')}
        </span> */}
      </div>
    );
  };

  const hasAddUserPermissions = () => {
    return !!hasPermission(
      'UserManagement_Users_Create'
    );
  };

  const hasEditUserPermissions = () => {
    return !!hasPermission(
      'UserManagement_Users_Update'
    );
  };

  const hasDeleteUserPermissions = () => {
    return !!hasPermission(
      'UserManagement_Users_Delete'
    );
  };

  const hasRoleToEditUser = (role: string) => {
    return roleList.resources.some(r => r.name === role);
  };

  const isAddUserHidden = () => {
    return !hasAddUserPermissions();
  };

  const isAddUserDisabled = () => {
    return !organisationSelected;
  };
  const isAddBulkUserDisabled = (bulkUserDetails:any) => {
     if(!organisationSelected){
      return true;
     }else{
      let checkVal = ['Completed','Failed']
      return bulkUserDetails && bulkUserDetails.length > 0 && !checkVal.includes(bulkUserDetails[0].status);
     } 
     
  };
  useEffect(() => {
    if (user) {
      setUserEmail(user.email);
    }
  }, [user]);

  const isRoleEditable = (data: any) => {
    return data?.email !== userEmail && hasRoleToEditUser(data.role);
  };

  const [isActionOpen, setIsActionOpen] = useState<boolean>(false);

  useEffect(() => {
    if (isActionOpen === true) {
      setIsActionOpen(false);
    }
  }, [isActionOpen]);

  useEffect(() => {
    if (uploadUserDataSuccess) {
      setTimeout(() => {
        setUploadUserDataSuccess(false);
      }, 2500);
    }
  }, [uploadUserDataSuccess]);

  const renderActions = (cellData: ICellData) => {
    const canEdit =
      hasEditUserPermissions() && hasRoleToEditUser(cellData.rowData.role);
    const canDelete =
      hasDeleteUserPermissions() &&
      isRoleEditable(cellData.rowData) &&
      hasRoleToEditUser(cellData.rowData.role);
    const areActionsAvailable = canEdit || canDelete;
    if (!areActionsAvailable) {
      return <></>;
    }
    return (
      <Popup
        className="popup-theme-wrap"
        element={
          <Icon
            root="common"
            name="ellipsis-horizontal"
            size="small"
            data-testid="actions-icon"
          />
        }
        hoverable={false}
        on="click"
      >
        <VerticalMenu>
          {canEdit && (
            <VerticalMenu.Item
              onClick={() => {
                setPanelSelectedRow(cellData.rowData);
                setSelectedModalMode(EModalOperations.UPDATE);
              }}
            >
              {i18next.t('editUser')}
            </VerticalMenu.Item>
          )}
          {canDelete && (
            <VerticalMenu.Item
              onClick={() => {
                setPanelSelectedRow(cellData.rowData);
                setDeleteModalOpen(true);
              }}
            >
              {i18next.t('delete')}
            </VerticalMenu.Item>
          )}
        </VerticalMenu>
      </Popup>
    );
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const [
    deleteUserResponse,
    isLoadingDeleteUser,
    errorDeleteUser,
    deleteUser
  ]: IUseFetch<IRoleResponse> = useFetch(
    `${props.baseURL}/api/v1/users/${
      panelSelectedRow ? panelSelectedRow.id : undefined
    }/orgs/${organisationSelected?.id ?? ''}/authorizations`,
    props.authFetch,
    undefined,
    true
  );

  useEffect(() => {
    if (
      !isLoadingDeleteUser &&
      errorDeleteUser === undefined &&
      deleteUserResponse !== null
    ) {
      fetchOrganisationUserList();
      setPanelSelectedRow(undefined);
      setDeleteModalOpen(false);
      toasts.success(i18next.t('deleteUserSuccess'));
    } else {
      if (errorDeleteUser) {
        toasts.error(i18next.t('errorFetchingUser'));
      }
    }
  }, [isLoadingDeleteUser]);

  const [
    deleteUnActivatedUserResponse,
    isLoadingDeleteingUnActivatedUser,
    errorUnActivatedUser,
    deleteUnActivatedUser
  ]: IUseFetch<boolean> = useFetch(
    `${props.baseURL}/api/v1/registered-users/orgs/${organisationSelected?.id ??
      ''}?userid=${panelSelectedRow ? panelSelectedRow.id : undefined}`,
    props.authFetch,
    undefined,
    true
  );

  useEffect(() => {
    if (
      !isLoadingDeleteingUnActivatedUser &&
      errorUnActivatedUser === undefined &&
      deleteUnActivatedUserResponse !== null
    ) {
      fetchOrganisationUserList();
      setPanelSelectedRow(undefined);
      setDeleteModalOpen(false);
      toasts.success(i18next.t('deleteUserSuccess'));
    } else {
      if (errorUnActivatedUser) {
        toasts.error(i18next.t('errorFetchingUser'));
      }
    }
  }, [isLoadingDeleteingUnActivatedUser]);
  const disabledTab = () => {
    let bulkResult = bulkUserDetails.length === 0 || bulkUserDetails.length > 0 && bulkUserDetails[0].status === 'Completed'
    return bulkResult
  }
  const deleteExistingUser = () => {
    const authorizations: any[] = [];
    const deleteUserObject = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(authorizations)
    };
    if (panelSelectedRow.status.toLowerCase() !== 'REGISTERED'.toLowerCase()) {
      deleteUserObject.method = 'DELETE';
      deleteUnActivatedUser(deleteUserObject);
    } else {
      deleteUser(deleteUserObject);
    }
  };
  const notify = (title: string, description: string, severity?: string) => {
    return (
      <div
        className={`ReactToastify-bulkUpload  ${
          !uploadUserDataSuccess ? 'ReactToastify-bulkUpload-exit' : ''
        }`}
      >
        {
          <ToastNotification
            title={title}
            description={description}
            closeToast={false}
            severity={severity}
            exiting={!uploadUserDataSuccess}
          />
        }
      </div>
    );
  };

  return (
    <>
      {uploadUserDataSuccess &&
        notify(
          i18next.t('userUploadSuccess'),
          i18next.t('userUploadProcessing'),
          'success'
        )}
      <div className="user-list-header-bar page-header-bar">
        <div className="page-header-bar-heading">
          <Select
            className="user-list-org-selector"
            options={orgDataToSelectOptions()}
            value={organisationSelected ? organisationSelected.id : ''}
            placeholder={
              !isLoadingOrganisationData ? i18next.t('selectOrg') : undefined
            }
            onChange={orgId => {
              setOrganisationSelected(selectOrgFromDropdown(orgId));
              dispatch(setSelectedOrganisation(selectOrgFromDropdown(orgId)));
              dispatch(setSelectedGatewayOrganisation(selectOrgFromDropdown(orgId)));          
            }}
            loading={isLoadingOrganisationData}
          />
        </div>
        <div className="page-header-bar-controls padded-controls">
          <Button
            onClick={() => {
              setNewUserOpen(true);
            }}
            hidden={isAddUserHidden()}
            disabled={isAddUserDisabled()}
          >
            {i18next.t('addUser')}
          </Button>
          {flags.bulkUploadUser && (
            <Button
              onClick={() => {
                setBulkUploadPopupOpen(true);
              }}
              hidden={isAddUserHidden()}
              disabled={isAddBulkUserDisabled(bulkUserDetails)}
            >
              {i18next.t('importUsers')}
            </Button>
          )}
        </div>
      </div>
      <div className="page-content-container with-header">
        <SplitPanelUtil>
          <Tab
            className={`UserList-container ${flags.bulkUploadUser ? 'displayBulkTab' : 'hideBulkTab'}`}
            activeIndex={activeTabIndex}
            onTabChange={activeIndex => {
              setActiveTabIndex(activeIndex)
              setBulkUserData([])
            }}
          >
            <Tab.Pane title="Users">
              <SplitPanelUtil.Table>
                <DataTable
                  data={data}
                  search={true}
                  searchPlaceholder={i18next.t('searchFor')}
                  resizableColumns={true}
                  loading={isLoadingOrganisationUsers}
                  emptyMessage={i18next.t('noRecordFound')}
                >
                  <DataTable.HeaderBar />
                  <DataTable.Column
                    field="displayName"
                    header={i18next.t('name')}
                    sortable={true}
                    renderer={invitationStatusRenderer}
                  />
                  <DataTable.Column
                    field="role"
                    header={i18next.t('role')}
                    sortable={true}
                  />
                  <DataTable.Column field="email" header={i18next.t('email')} />
                  <DataTable.Column field="phone" header={i18next.t('phone')} />
                  <DataTable.Column
                    field="registrationStatus"
                    header={i18next.t('status')}
                    renderer={userStatusRenderer}
                  />
                  <DataTable.Column
                    field="actions"
                    header={i18next.t('actions')}
                    renderer={renderActions}
                  />
                  <DataTable.Pagination
                    totalItems={0}
                    showDisplayDetails={true}
                    itemsPerPage={itemsPerPage}
                    // itemsPerPageOptions={[10, 15, 18]}
                    onPageChange={(...all) => {
                      // console.log('page, itemsPerPage', all);
                    }}
                  />
                </DataTable>
              </SplitPanelUtil.Table>
            </Tab.Pane>
            <Tab.Pane title="Bulk Upload Status" disabled={disabledTab()}>
            <SplitPanelUtil.Table>
                <DataTable
                  data={getBulkUserdata}
                  search={true}
                  searchPlaceholder={i18next.t('searchFor')}
                  resizableColumns={true}
                  loading={isLoadingOrganisationUsers}
                  emptyMessage={i18next.t('noRecordFound')}
                >
                  <DataTable.HeaderBar />
                  <DataTable.Column field="role" header={i18next.t('role')} sortable={true}/>
                  <DataTable.Column field="email" header={i18next.t('email')} sortable={true}/>
                  <DataTable.Column field="status" header={i18next.t('status')} renderer={bulkUserStatusRenderer} sortable={true}/>
                  <DataTable.Column field="fileName" header={i18next.t('fileName')}/>
                  <DataTable.Pagination
                    totalItems={0}
                    showDisplayDetails={true}
                    itemsPerPage={18}
                    // itemsPerPageOptions={[10, 15, 18]}
                    onPageChange={(...all) => {
                      // console.log('page, itemsPerPage', all);
                    }}
                  />
                </DataTable>
              </SplitPanelUtil.Table>
            </Tab.Pane>
          </Tab>
          {panelSelectedRow && organisationSelected && !deleteModalOpen && (
            <UserModal
              mode={selectedModalMode}
              onClose={() => {
                setPanelSelectedRow(undefined);
                setSelectedModalMode(EModalOperations.READ);
              }}
              onSave={fetchOrganisationUserList}
              baseURL={props.baseURL}
              authFetch={props.authFetch}
              offering={props.offering}
              organisation={organisationSelected}
              panelSelectedRow={panelSelectedRow}
              setDeleteModalOpen={setDeleteModalOpen}
              isRoleEditable={isRoleEditable}
            />
          )}

          {newUserOpen && organisationSelected && (
            <UserModal
              mode={EModalOperations.CREATE}
              onClose={setNewUserOpen}
              onSave={fetchOrganisationUserList}
              baseURL={props.baseURL}
              authFetch={props.authFetch}
              offering={props.offering}
              organisation={organisationSelected}
              setDeleteModalOpen={setDeleteModalOpen}
              isRoleEditable={isRoleEditable}
            />
          )}
        </SplitPanelUtil>
      </div>
      {deleteModalOpen && (
        <DeleteAlertModal
          onClose={() => {
            setDeleteModalOpen(false);
            setPanelSelectedRow(undefined);
          }}
          onConfirm={() => {
            deleteExistingUser();
          }}
          isLoadingDeleteUser={isLoadingDeleteUser}
          displayName={panelSelectedRow.displayName}
        />
      )}
      {getBulkUpladPopupOpen && organisationSelected && (
        <BulkUpladUserModal
          onClose={() => {
            setBulkUploadPopupOpen(false);
          }}
          onConfirm={() => {}}
          baseURL={props.baseURL}
          panelSelectedRow={panelSelectedRow}
          organisationSelected={organisationSelected}
          offering={props.offering}
          authFetch={props.authFetch}
          isLoadingDeleteUser={isLoadingDeleteUser}
          setUploadUserDataSuccess={setUploadUserDataSuccess}
        />
      )}
    </>
  );
};
export default consumeAPIs(UserList);
